import type { MapObject } from './types'

/**
 * Bundled per-id defaults and shared predicates for map pins.
 *
 * Pin-type MEMBERSHIP, display NAMES and GROUPING all come from the Remote
 * Config catalog (`pinTypeCatalog.ts`, built by `buildPinTypeCatalog` from a
 * `mapPinTypes2` fetch) — this file no longer holds a static list of them.
 * What stays here is what the catalog does not carry: the default colour a
 * new pin of a given id gets (`PIN_DEFAULT_COLORS` / `pinDefaultColor`), and a
 * handful of small predicates shared across surfaces (`isStandPin`,
 * `pinTypeVisibility`, `isTrailCameraPin`) that would otherwise be
 * re-implemented per surface.
 *
 * The pin *icon* is resolved separately from the SVG assets in
 * `/public/assets/map_pin_<type>.svg` (see MapPin.tsx).
 */

/**
 * Default colour per pin type id — the colour a new pin of that type gets and
 * the tint a pin without its own colour renders with. Not in the Remote Config
 * catalog on either platform; mobile keeps the same kind of table in
 * PinIconHelper. Unknown ids take `other`.
 */
export const PIN_DEFAULT_COLORS: Readonly<Record<string, string>> = {
  tower: '#6b4423', stand: '#10A911', pin: '#10A911', camera: '#3a85d6', camera_shared: '#FF681F',
  parking: '#3a5a3f', gathering_place: '#5a3a2a', road_barrier: '#4a3a6a', bait: '#3a6a3a',
  bridge: '#4a4a5a', fireplace: '#B60B1E', crossing: '#4D4052', salt_stone: '#7a7a6a',
  feeding_grounds: '#5a7a3a', target: '#3a5a3f', mushroom: '#FF681F', berries: '#2E2A6B',
  trap: '#990066', other: '#747a76', custom: '#10A911',
  moose: '#20502b', red_deer: '#7a4a2a', roe_deer: '#8a6d3a', fallow_deer: '#a8854a',
  white_tailed_deer: '#6b7b6e', reindeer: '#8B572A', wild_boar: '#ff681f', bear: '#5a3a2a',
  fox: '#c0531f', hare: '#7d6a52', pheasant: '#3a6a3a', fish: '#3a6a7a', badger: '#4a4a4a',
  burrow: '#6b4423', eggs: '#3a6a3a', tracks: '#8B572A', scat: '#6b4423',
  arrow_n: '#5a6a7a', arrow_ne: '#5a6a7a', arrow_e: '#5a6a7a', arrow_se: '#5a6a7a',
  arrow_s: '#5a6a7a', arrow_sw: '#5a6a7a', arrow_w: '#5a6a7a', arrow_nw: '#5a6a7a',
}

export function pinDefaultColor(type: string): string {
  return PIN_DEFAULT_COLORS[type] ?? PIN_DEFAULT_COLORS.other
}

/**
 * CLUB-157: the only pin types that can be a drive stand are stands & towers.
 * Case-insensitive, matching the mobile app's stand filter
 * (`map_pins_filter_data_source.dart`: `type.toLowerCase() == 'stand' || 'tower'`).
 * Every stand-picking surface (drive editor, stand map picker) filters with this
 * so the taxonomy can never diverge per-surface.
 */
export function isStandPin(pin: Pick<MapObject, 'type'>): boolean {
  const t = pin.type.toLowerCase()
  return t === 'stand' || t === 'tower'
}

/**
 * CLUB-667: a visibility test against the user's hidden set, shared by every
 * surface that honours the pin-type filter (the canvas and the print payload)
 * so none of them re-implements the predicate.
 *
 * Returns a predicate rather than taking `(type, hidden)` so the Set is built
 * once per render instead of once per pin — the canvas calls this across every
 * object on the ground. The common case (nothing hidden) returns a shared
 * always-true predicate and allocates nothing at all.
 *
 * A *hidden* set, not a visible one: `MapObject['type']` is far wider than the
 * ids the pin-type catalog lists, so a type with no entry there — `custom`, a
 * legacy alias, anything mobile adds later — stays visible rather than
 * vanishing.
 */
export function pinTypeVisibility(
  hiddenPinTypes: readonly string[],
): (type: MapObject['type']) => boolean {
  if (hiddenPinTypes.length === 0) return ALWAYS_VISIBLE
  const hidden = new Set(hiddenPinTypes)
  return (type) => !hidden.has(type)
}

const ALWAYS_VISIBLE = () => true

/**
 * Namespace for a trail camera's id when it is merged into the same object list
 * as a team's map pins.
 *
 * A camera is drawn on the same map as the pins, but it is not one of them: its
 * position lives in the camera service, no client writes a camera into the
 * `mapPins` collection, and the map editor is deliberately never handed one
 * because the map-pin API cannot save it. The prefix is what tells the two
 * apart in a merged list — the pin *type* cannot, because a hunter can place an
 * ordinary map pin that marks a camera, and that one is theirs to move and save.
 *
 * The value is shared wire vocabulary rather than presentation, but it lives
 * here because both the UI that counts these and the client that mints them
 * need it, and this package is the one they can both depend on.
 */
export const TRAIL_CAMERA_PIN_ID_PREFIX = 'camera:'

/** Whether a map object is a trail camera rather than a map-pin document. */
export function isTrailCameraPin(object: Pick<MapObject, 'id'>): boolean {
  return object.id.startsWith(TRAIL_CAMERA_PIN_ID_PREFIX)
}
